import React, { useEffect, useState } from "react";
import { FootprintsIcon as Footprints, ToggleLeftIcon as ToggleLeft, ToggleRightIcon as ToggleRight, StopCircleIcon as StopCircle, MapTrifoldIcon as MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import BuyerLiveWalkMap from "@/components/BuyerLiveWalkMap";

/**
 * Buyer-side controls for in-app live walk tracking on a single booking.
 *
 * In-app only — no public no-auth share link exists by design. The
 * map renders inline below the badge when the buyer expands it.
 *
 * States:
 *   1. Consent OFF                  → "Allow live walk tracking" toggle
 *   2. Consent ON, no walk yet      → toggle stays visible
 *   3. Consent ON, active walk      → live badge + "View live map" toggle
 *   4. Consent ON, ended walk (24h) → "View walk recap" toggle
 *
 * Switching consent OFF instantly revokes any in-flight walk (backend
 * handles this transactionally inside the PATCH endpoint).
 */
const POLL_MS = 15_000;

export default function BuyerLiveWalkBadge({ bookingId, initialConsent }) {
  const [walk, setWalk] = useState(null);
  const [consent, setConsent] = useState(Boolean(initialConsent));
  const [busy, setBusy] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}/walk`);
        if (cancelled) return;
        setWalk(data?.walk || null);
        if (data?.walk?.status === "ended" && timer) {
          clearInterval(timer);
          timer = null;
        }
      } catch (err) {
        if (timer) clearInterval(timer);
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [bookingId]);

  const setConsentValue = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${bookingId}/live-walk-consent`, { consent: next });
      setConsent(next);
      toast.success(
        next
          ? "Live walk tracking enabled. The walker can now share a live map with you."
          : "Live walk tracking disabled. Any in-progress share has been revoked.",
      );
      if (!next && walk?.status === "active") {
        setWalk({ ...walk, status: "ended", revoked: true });
        setMapOpen(false);
      }
    } catch (err) {
      toast.error("Couldn't update tracking consent — try again?");
    } finally {
      setBusy(false);
    }
  };

  const revokeActiveWalk = async () => {
    if (!window.confirm("Stop the live walk share immediately? The walker can keep walking, but you'll no longer see live location.")) {
      return;
    }
    setBusy(true);
    try {
      // Hitting consent-PATCH=false revokes any active walk on this
      // booking server-side AND toggles consent off in one shot. The
      // buyer can flip it back on later for a future walk.
      await api.patch(`/bookings/${bookingId}/live-walk-consent`, { consent: false });
      setConsent(false);
      setWalk(walk ? { ...walk, status: "ended", revoked: true } : null);
      setMapOpen(false);
      toast.success("Live walk share revoked.");
    } catch (err) {
      toast.error("Couldn't revoke right now — try again?");
    } finally {
      setBusy(false);
    }
  };

  // ----- Render: walk is active -----
  if (consent && walk?.status === "active" && !walk?.revoked) {
    return (
      <div data-testid={`buyer-walk-badge-${bookingId}-active`}>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setMapOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 hover:bg-emerald-100 px-4 py-2 text-[12.5px] font-semibold"
            data-testid={`buyer-walk-toggle-map-${bookingId}`}
          >
            <Footprints size={14} weight="duotone" />
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-80 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Live walk · {mapOpen ? "hide map" : "view map"}
            <MapTrifold size={13} weight="duotone" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={revokeActiveWalk}
            className="inline-flex items-center gap-1.5 text-[12px] text-crimson hover:underline underline-offset-2 disabled:opacity-50"
            data-testid={`buyer-revoke-walk-${bookingId}`}
          >
            <StopCircle size={13} weight="fill" />
            Stop sharing now
          </button>
        </div>
        {mapOpen ? (
          <BuyerLiveWalkMap bookingId={bookingId} onClose={() => setMapOpen(false)} />
        ) : null}
      </div>
    );
  }

  // ----- Render: walk ended within 24h window -----
  if (consent && walk?.status === "ended" && !walk?.revoked) {
    return (
      <div data-testid={`buyer-walk-badge-${bookingId}-ended`}>
        <button
          type="button"
          onClick={() => setMapOpen((v) => !v)}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream/60 border border-gold/40 text-gold-dark hover:bg-cream px-4 py-2 text-[12.5px] font-semibold"
          data-testid={`buyer-walk-recap-toggle-${bookingId}`}
        >
          <Footprints size={14} weight="duotone" />
          {mapOpen ? "Hide walk recap" : `View walk recap · ${walk?.summary?.point_count ?? 0} GPS pts`}
          <MapTrifold size={13} weight="duotone" />
        </button>
        {mapOpen ? (
          <BuyerLiveWalkMap bookingId={bookingId} onClose={() => setMapOpen(false)} />
        ) : null}
      </div>
    );
  }

  // ----- Render: consent toggle (no walk in flight, or revoked) -----
  return (
    <div
      className="mt-3 inline-flex items-center gap-2.5 rounded-lg border border-[#E2DAC2] bg-cream/40 px-3.5 py-2"
      data-testid={`buyer-walk-consent-${bookingId}`}
    >
      <Footprints size={14} weight="duotone" className="text-gold-dark flex-shrink-0" />
      <span className="text-[12.5px] text-[#3F3F3F]">
        Live walk tracking
        <span className="text-[#6B6B6B]"> · see a live map from the walker</span>
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => setConsentValue(!consent)}
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-bold uppercase tracking-wider transition-colors ${
          consent
            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
            : "bg-white border border-[#D9CFB0] text-[#6B6B6B] hover:text-ink hover:border-ink"
        } disabled:opacity-50`}
        data-testid={`buyer-walk-consent-toggle-${bookingId}`}
        aria-pressed={consent}
      >
        {consent ? (
          <><ToggleRight size={14} weight="fill" /> On</>
        ) : (
          <><ToggleLeft size={14} weight="fill" /> Off</>
        )}
      </button>
    </div>
  );
}
